import React, { useState } from "react";

import Typography from "@mui/material/Typography";

import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useSlackIntegration from "@app/analytics/hooks/integrations/useSlackIntegration";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import DistributionSettingPreview from "@app/analytics/Integrations/components/DistributionSettingPreview";
import IntegrationDetailHeader from "@app/analytics/Integrations/components/IntegrationDetailHeader";
import IntegrationError from "@app/analytics/Integrations/components/IntegrationError";
import IntegrationNotConnected from "@app/analytics/Integrations/components/IntegrationNotConnected";
import { PROVIDER_CONTENT } from "@app/analytics/Integrations/constants";
import useConnectIntegrationToasts from "@app/analytics/Integrations/hooks/useConnectIntegrationToasts";
import {
  IntegrationDetailContent,
  List,
  LoadingIntegrationDetail,
} from "@app/analytics/Integrations/styles";
import * as IntegrationUtils from "@app/analytics/Integrations/utils";
import { MESSAGING_BOT_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";

import Alert from "@rui/Alert";
import ConfirmationModal from "@rui/ConfirmationModal";
import { Link } from "@rui/Link";
import { Column } from "@rui/Stack";

const UserSlackIntegrationConfig = () => {
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const { authorize } = useIntegration();
  const { userSettings } = useUserSettings();
  const {
    userIntegration: integration,
    workspaceIntegration,
    disconnect,
    isLoading,
  } = useSlackIntegration();

  useConnectIntegrationToasts({
    integration,
    targetAction: MESSAGING_BOT_PROVIDERS_BY_ID.slack_bot.connectAction,
  });

  if (isLoading) {
    return <LoadingIntegrationDetail />;
  }

  if (!integration) {
    return <IntegrationError />;
  }

  const handleClickConnect = () => {
    authorize.mutate({
      providerId: integration.providerId,
      returnRoute: integration.route,
      scope: "user",
    });
  };

  const handleDisconnect = () => {
    disconnect.mutate("user", {
      onSuccess: () => setConfirmingDisconnect(false),
      onError: () => {
        setConfirmingDisconnect(false);
        toast.error(
          `There was an error disconnecting ${integration.displayName}. Please try again or log into your ${integration.displayName} account to disconnect Read.`
        );
      },
    });
  };

  const handleClickDisconnect = () => setConfirmingDisconnect(true);

  const providerContent = PROVIDER_CONTENT[integration.providerId];

  const recapRecipients =
    IntegrationUtils.getMeetingRecapRecipientsDescriptionFromUserSettings({
      userSettings,
      provider: "slack",
    });
  const preReadRecipients =
    IntegrationUtils.getMeetingPreReadRecipientsDescriptionFromUserSettings({
      userSettings,
      provider: "slack",
    });
  const topicReportRecipients =
    IntegrationUtils.getTopicReportRecipientsDescriptionFromUserSettings({
      userSettings,
      provider: "slack",
    });

  const connectCta = {
    onClick: handleClickConnect,
    isLoading: authorize.isPending,
  };

  return (
    <>
      <IntegrationDetailHeader
        cta={connectCta}
        provider={integration}
        documentationUrl={integration.documentationUrl}
        onClickDisconnect={handleClickDisconnect}
        onReauth={handleClickConnect}
        isDisconnecting={disconnect.isPending}
      >
        <Typography variant="bodySecondaryReg" color="shades.neutral.600">
          {providerContent.headerDescription ??
            providerContent.shortDescription}
        </Typography>
      </IntegrationDetailHeader>
      {integration.isConnected ? (
        <IntegrationDetailContent spacing={3}>
          <Column spacing={1}>
            {integration.providerEmail && (
              <Typography variant="bodyPrimaryMed" color="shades.neutral.800">
                {integration.providerEmail} is connected
              </Typography>
            )}
            <Typography variant="bodySecondaryReg" color="shades.neutral.600">
              You have successfully connected Read to{" "}
              {integration.displayNameShort}. The Read App can automatically
              message meeting recaps to you and other meeting participants.
            </Typography>
          </Column>
          <Column spacing={1.5}>
            <Typography variant="bodySecondaryMed" color="shades.neutral.800">
              Your settings
            </Typography>
            <Typography variant="bodySecondaryReg" color="shades.neutral.600">
              <List>
                <DistributionSettingPreview
                  settingName="meeting recaps"
                  recipients={recapRecipients}
                  integrationDisplayName={integration.displayNameShort}
                />
                <DistributionSettingPreview
                  settingName="meeting Pre-Reads"
                  recipients={preReadRecipients}
                  integrationDisplayName={integration.displayNameShort}
                />
                <DistributionSettingPreview
                  settingName="topic summaries"
                  recipients={topicReportRecipients}
                  integrationDisplayName={integration.displayNameShort}
                />
              </List>
            </Typography>
          </Column>
        </IntegrationDetailContent>
      ) : (
        <IntegrationNotConnected cta={connectCta} provider={integration} />
      )}
      {!integration.isDisabled && (
        <ConfirmationModal
          title="Remove Slack connection?"
          open={confirmingDisconnect}
          onClose={() => setConfirmingDisconnect(false)}
          onConfirm={handleDisconnect}
          size="md"
          confirmProps={{ label: "Yes, remove" }}
          cancelProps={{ type: "negative" }}
          loading={disconnect.isPending}
        >
          <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
            Are you sure you want to remove {integration.displayName} from your
            Read connected apps?
          </Typography>
          {workspaceIntegration.isConnected && (
            <Alert embedded severity="info" alignItems="flex-start">
              Your Workspace has enabled the{" "}
              <Link to={workspaceIntegration.route}>Slack integration</Link>, so
              meeting notes may still be posted to Slack based on your{" "}
              <Link to={ROUTES.USER_SETTINGS_REPORTING}>
                distribution settings
              </Link>
              .
            </Alert>
          )}
        </ConfirmationModal>
      )}
    </>
  );
};

export default UserSlackIntegrationConfig;
